import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * <orivon-add-lines-component>: adds several variants in ONE /cart/add.js request ("Add all 6"), on Horizon's
 * cart contract (plan §2.6, mirroring assets/product-form.js). The CartLinesUpdateEvent it dispatches is what
 * Horizon's cart count, cart drawer (morph + auto-open) and quick add already listen to.
 * Markup comes from snippets/orivon-buy-button.liquid (`variants` param):
 *
 *   <orivon-add-lines-component data-lines="{variantId}:{qty},{variantId}:{qty}" data-added-message="Added"
 *     data-error-message="…" data-network-error-message="…">
 *     <button type="button" ref="button" on:click="/handleClick">…</button>
 *     <p class="o-buy-button__error hidden" ref="error"></p>
 *     <div class="visually-hidden" aria-live="polite" role="status" ref="liveRegion"></div>
 *   </orivon-add-lines-component>
 *
 * While the request runs the button is .is-loading + aria-busy; after a successful add it shows "Added"
 * (data-added="true", the same state Horizon's add-to-cart buttons use) for 1.6s. Errors (e.g. sold out) show in
 * the error element and the live region, dispatch CartErrorEvent, and resolve the event with didError: true.
 * The texts come from locales/en.default.json through the data attributes (orivon.buy_button.error_add /
 * error_network, actions.added); the English strings here are fallbacks only.
 *
 * @typedef {object} AddLinesRefs
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} [error]
 * @property {HTMLElement} [liveRegion]
 *
 * @extends {Component<AddLinesRefs>}
 */
export class OrivonAddLinesComponent extends Component {
  requiredRefs = ['button'];

  #busy = false;
  /** @type {number | undefined} */
  #addedTimer;
  /** @type {number | undefined} */
  #errorTimer;

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#addedTimer);
    window.clearTimeout(this.#errorTimer);
  }

  /** @returns {{ id: number, quantity: number }[]} */
  get items() {
    return (this.dataset.lines || '')
      .split(',')
      .map((pair) => {
        const [id, quantity] = pair.split(':');
        return { id: Number(id), quantity: Math.max(1, parseInt(quantity || '1', 10) || 1) };
      })
      .filter((item) => item.id > 0);
  }

  /** @param {Event} [event] */
  async handleClick(event) {
    event?.preventDefault();

    const { button } = this.refs;
    if (this.#busy || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    const items = this.items;
    if (!items.length) return;

    this.#busy = true;
    this.#setError('');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);
    const itemCount = items.reduce((total, item) => total + item.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify({ items, sections: sections.join(',') }) })
      );
      const result = await response.json();

      if (result.status) {
        // Shopify AJAX error, e.g. 422 "… is already sold out."
        const message =
          result.description ||
          result.message ||
          this.dataset.errorMessage ||
          'Could not add these products to your cart.';
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        this.#setError(message);

        const ajaxCart = await this.#fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            itemCount,
            source: 'orivon-add-lines',
            sourceId: this.id,
            didError: true,
          },
        });
        return;
      }

      const ajaxCart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          sections: result.sections,
          itemCount,
          source: 'orivon-add-lines',
          sourceId: this.id,
          didError: false,
        },
      });
      this.#showAdded();
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#setError(this.dataset.networkErrorMessage || 'Could not reach the cart. Please try again.');
    } finally {
      this.#busy = false;
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  }

  /** The cart after the add: through Horizon's cart-items-component when present, else /cart.json. */
  async #fetchCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      if (typeof (/** @type {any} */ (cartItems).fetchCartData) === 'function') {
        return /** @type {any} */ (cartItems).fetchCartData();
      }
    }
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  #showAdded() {
    const { button } = this.refs;
    button.dataset.added = 'true';
    this.#announce(this.dataset.addedMessage || Theme.translations?.added || 'Added');
    window.clearTimeout(this.#addedTimer);
    this.#addedTimer = window.setTimeout(() => {
      button.removeAttribute('data-added');
      this.#announce('');
    }, 1600);
  }

  /** @param {string} message - Empty hides the error. */
  #setError(message) {
    const { error } = this.refs;
    window.clearTimeout(this.#errorTimer);
    if (error) {
      error.textContent = message;
      error.classList.toggle('hidden', !message);
    }
    if (!message) return;
    this.#announce(message);
    this.#errorTimer = window.setTimeout(() => this.#setError(''), 6000);
  }

  /** @param {string} text */
  #announce(text) {
    const { liveRegion } = this.refs;
    if (liveRegion) liveRegion.textContent = text;
  }
}

if (!customElements.get('orivon-add-lines-component')) {
  customElements.define('orivon-add-lines-component', OrivonAddLinesComponent);
}
